import React, { useMemo } from 'react'
import { PageProps } from 'gatsby'
import useSWR from 'swr'
import { useShallow } from 'zustand/react/shallow'
import { Layout } from '../common'
import FirmHeader from './firm/header'
import { Firm } from './firm/mapper'
import FirmTab from './firm/firmTab'
import { NonClientTemplate } from './nonClientTemplate/NonClientTemplate'
import { baseFetcher } from '../../Utils/api'
import { FirmInfosResult, FirmTeaserMapper } from '../home/mapper'
import { FreeTemplate } from './freeTemplate/FreeTemplate'
import Button from '../ui/button'
import {
  isAttractivitySubscription,
  isFreeSubscription,
  isPremiumSubscription,
} from '../../Utils/firm/subscription'
import { useNotificationSubscriptionStore } from '../../stores/notification/notificationSubscriptionStore'

export type entriesProps = {
  content: Firm
  googleMapsApiKey: string | null
  youTubeApiKey: string | null
}

export type FirmTemplateProps = {
  data: Firm
  isCareerSite: boolean
  constants: {
    siteStrapiUrl: string
    frontStrapiToken: string
    googleMapsApiKey: string | null
    recaptchaKey: string | null
    youTubeApiKey: string | null
  }
  location: PageProps['location']
}

const FirmTemplate: React.FC<FirmTemplateProps> = ({
  data,
  constants,
  isCareerSite,
  location,
}) => {
  const subscription = data?.subscription
  const isPremium = isPremiumSubscription({
    subscription,
    isPremium: data?.premium || false,
  })
  const isFree = isFreeSubscription({
    subscription,
    isPremium: data?.premium || false,
  })
  const isAttractivity = isAttractivitySubscription({
    subscription,
    isPremium: data?.premium || false,
  })

  const openNewClientNotificationSubscriptionModal =
    useNotificationSubscriptionStore(
      useShallow((state) => state.openNewClientNotificationSubscriptionModal),
    )

  const { data: list = [] } = useSWR<FirmInfosResult[]>(
    `${constants.siteStrapiUrl}/api/infos-for-home`,
    baseFetcher,
  )

  const firmsToDisplay = useMemo(
    () => (list || []).map(FirmTeaserMapper),
    [list],
  )

  if (isPremium || isAttractivity) {
    return (
      <Layout
        careerSite={isCareerSite}
        colorCareerSite={data?.colorCareerSite || undefined}
        apiToken={constants?.frontStrapiToken}
        apiUrl={constants?.siteStrapiUrl}
      >
        <FirmHeader firm={data} careerSite={isCareerSite} />
        <div className="mt-8 lg:mt-24">
          <FirmTab
            entry={{
              content: data,
              googleMapsApiKey: constants.googleMapsApiKey || '',
              youTubeApiKey: constants.youTubeApiKey,
            }}
            color={
              isCareerSite && data.colorCareerSite ? data.colorCareerSite : null
            }
            constants={constants}
            location={location}
          />
        </div>
      </Layout>
    )
  }
  if (isFree) {
    return (
      <Layout
        careerSite={isCareerSite}
        colorCareerSite={data?.colorCareerSite || undefined}
        apiToken={constants?.frontStrapiToken}
        apiUrl={constants?.siteStrapiUrl}
      >
        <FirmHeader firm={data} careerSite={isCareerSite} />
        <FreeTemplate
          constants={constants}
          data={data}
          othersFirms={firmsToDisplay}
          location={location}
          youTubeApiKey={constants.youTubeApiKey}
        />
      </Layout>
    )
  }

  return (
    <Layout
      careerSite={isCareerSite}
      colorCareerSite={data?.colorCareerSite || undefined}
      apiToken={constants?.frontStrapiToken}
      apiUrl={constants?.siteStrapiUrl}
    >
      <FirmHeader
        firm={data}
        careerSite={isCareerSite}
        customButton={
          <Button
            color="primary"
            label="Alertez moi lorsqu'il sera référencé"
            invert
            onClick={() =>
              openNewClientNotificationSubscriptionModal(data.uuid || '')
            }
          />
        }
      />
      <NonClientTemplate
        constants={constants}
        data={data}
        othersFirms={firmsToDisplay}
        youTubeApiKey={constants.youTubeApiKey}
      />
    </Layout>
  )
}

export default FirmTemplate
